import * as R from 'ramda';
import { Rnd } from 'react-rnd';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import React, { lazy, Suspense } from 'react';
import { createStructuredSelector } from 'reselect';
import { pure, compose, lifecycle, withHandlers } from 'react-recompose';
// components
import { closeModal } from '../../components/modal/actions';
import DimmerComponent from '../../components/loader/dimmer';
import { ResizeHandleComponent } from '../../components/resizable/settings';
// common
import {
  visitNewDoPage,
  visitCloListPage,
  visitTelListPage,
  visitFuelCardsPage,
  visitLoadBoardsPage,
  visitTollChargesPage,
  visitDriverCardsPage,
  visitNewDOQuoteEditPage,
  visitDispatchBoardCloPage,
  visitCarrierProfilePageV2,
  visitDispatchBoardTelPage,
  visitFleetProfileTruckPage,
  visitFleetProfileDriverPage,
  visitFleetProfileVendorPage,
  visitFleetProfileTrailerPage,
  visitDispatchDetailsLoadPage,
  visitDispatchDetailsOrderPage,
  visitCarrierContractDetailsPage,
  visitCustomerContractDetailsPage,
  visitClaimManagementClaimDetailsPage,
} from '../../common/actions';
// features
import { visitPage as visitDriverCardsV2Page } from '../driver-cards-v2/actions';
import { getLoadDispatchBoardDetailsRequest } from '../dispatch-board/load/actions';
import { getOrderDispatchBoardDetailsRequest } from '../dispatch-board/order/actions';
import { getLoadDriverCarrierRateListRequest } from '../dispatch-details/load/actions';
import { getOrderDetailsRequest as getDispatchDetailsOrderRequest } from '../dispatch-details/order/actions';
import {
  resetCloEventListAndPagination,
  refreshCloEventListWithSelectedEvents,
} from '../dispatch-planner-events-v2/actions';
import {
  getCustomerPortalListRequest,
  resetListAndPagination as resetCustomerPortalListAndPagination,
} from '../customer-portal/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// icons
import * as I from '../../svgs';
// ui
import { Box, Flex, Button, FixedBox, AbsoluteBox } from '../../ui';
// utilities
import routesMap from '../../utilities/routes';
// feature expanded container
import ErrorMessage from './components/error-message';
import { makeSelectExpandedContainerOptions } from './selectors';
import { setExpandedContainerError, setExpandedContainerOptions } from './actions';
//////////////////////////////////////////////////

// features
const NewDOComponent = lazy(() => import('../new-do'));
const LoadBoards = lazy(() => import('../load-board'));
const DriversCard = lazy(() => import('../drivers-card'));
const DriverCardsV2 = lazy(() => import('../driver-cards-v2'));
const NewQuoteComponent = lazy(() => import('../new-do-quote'));
const FuelCardListComponent = lazy(() => import('../fuel-cards'));
const CarrierScorecards = lazy(() => import('../carrier-scorecards'));
const TollChargesListComponent = lazy(() => import('../toll-charges'));
const CarrierStatisticReport = lazy(() => import('../carrier-statistic-report'));
const LoadDetailsNewComponent = lazy(() => import('../dispatch-details/load'));
const OrderDetailsNewComponent = lazy(() => import('../dispatch-details/order'));
const CarrierRateRequestReport = lazy(() => import('../carrier-rate-request-report'));
const ClaimManagementClaimDetails = lazy(() => import('../claim-management/details'));
// fleet-v2
const FleetProfileTruck = lazy(() => import('../fleet-profile/truck'));
const FleetProfileDriver = lazy(() => import('../fleet-profile/driver'));
const FleetProfileVendor = lazy(() => import('../fleet-profile/vendor'));
const FleetProfileTrailer = lazy(() => import('../fleet-profile/trailer'));
// carrier
const CarrierProfile = lazy(() => import('../carrier-profile'));
const CarrierContractDetails = lazy(() => import('../carrier-profile/contracts/carrier'));
const CustomerContractDetails = lazy(() => import('../carrier-profile/contracts/customer'));

const mapStateToProps = (state) => createStructuredSelector({
  expandedContainerOptions: makeSelectExpandedContainerOptions(state),
});

const withRouter = (Component) => (props) => {
  const location = useLocation();

  return <Component {...props} location={location} />;
};

const enhance = compose(
  withRouter,
  connect(
    mapStateToProps,
    {
      closeModal,
      visitNewDoPage,
      visitCloListPage,
      visitTelListPage,
      visitFuelCardsPage,
      visitLoadBoardsPage,
      visitTollChargesPage,
      visitDriverCardsPage,
      visitDriverCardsV2Page,
      visitNewDOQuoteEditPage,
      visitCarrierProfilePageV2,
      setExpandedContainerError,
      visitDispatchBoardCloPage,
      visitDispatchBoardTelPage,
      // socketTelDisconnectRequest,
      visitFleetProfileTruckPage,
      visitFleetProfileDriverPage,
      visitFleetProfileVendorPage,
      setExpandedContainerOptions,
      visitFleetProfileTrailerPage,
      visitDispatchDetailsLoadPage,
      getCustomerPortalListRequest,
      visitDispatchDetailsOrderPage,
      getDispatchDetailsOrderRequest,
      resetCloEventListAndPagination,
      visitCarrierContractDetailsPage,
      visitCustomerContractDetailsPage,
      getLoadDispatchBoardDetailsRequest,
      getOrderDispatchBoardDetailsRequest,
      getLoadDriverCarrierRateListRequest,
      resetCustomerPortalListAndPagination,
      visitClaimManagementClaimDetailsPage,
      refreshCloEventListWithSelectedEvents,
    },
  ),
  withHandlers({
    handleGoBack: (props) => () => {
      const { expandedContainerOptions, setExpandedContainerOptions } = props;

      const componentsHistory = R.propOr([], 'componentsHistory', expandedContainerOptions);
      const prevComponent = R.prop(R.subtract(R.length(componentsHistory), 2), componentsHistory);

      if (G.isNilOrEmpty(prevComponent)) return;

      const { componentType, visitPageGuid } = prevComponent;

      setExpandedContainerOptions({
        ...expandedContainerOptions,
        componentType,
        visitPageGuid,
        componentsHistory: R.dropLast(2, componentsHistory),
      });
    },
    handleCloseExpandedContainer: (props) => (notUseVisitPageAction = false) => {
      const {
        location,
        visitCloListPage,
        visitTelListPage,
        // visitDriverCardsPage,
        setExpandedContainerError,
        visitDispatchBoardCloPage,
        visitDispatchBoardTelPage,
        // socketTelDisconnectRequest,
        setExpandedContainerOptions,
        getCustomerPortalListRequest,
        getDispatchDetailsOrderRequest,
        resetCloEventListAndPagination,
        getLoadDispatchBoardDetailsRequest,
        getOrderDispatchBoardDetailsRequest,
        getLoadDriverCarrierRateListRequest,
        visitClaimManagementClaimDetailsPage,
        resetCustomerPortalListAndPagination,
        refreshCloEventListWithSelectedEvents,
        expandedContainerOptions: { options, componentType },
      } = props;

      const guid = G.getGuidFromObject(options);

      const routePath = G.getPropFromObject('pathname', location);

      const dispatchDetailsOpened = R.includes(
        componentType,
        [GC.PAGE_DISPATCH_DETAILS_NEW_LOAD, GC.PAGE_DISPATCH_DETAILS_NEW_ORDER],
      );

      const visitPageActionsMap = {
        // TODO: check how to handle close here
        // [GC.ROUTE_PATH_DRIVERS_CARD]: visitDriverCardsPage,
        [GC.ROUTE_PATH_CLO_REPORT_LIST]: () => {
          if (G.isTrue(G.getPropFromObject('fromPivotTable', options))) return;

          visitCloListPage({ reportType: GC.CLO_REPORT });
        },
        [GC.ROUTE_PATH_TEL_REPORT_LIST]: () => {
          if (G.isTrue(G.getPropFromObject('fromPivotTable', options))) return;

          visitTelListPage({ reportType: GC.TEL_REPORT });
        },
        [GC.ROUTE_PATH_DISPATCH_BOARD_TEL]: () => {
          const guid = G.getGuidFromObject(options);

          if (G.isNotNilAndNotEmpty(guid)) {
            getLoadDispatchBoardDetailsRequest(guid);
          } else {
            visitDispatchBoardTelPage();
          }
        },
        [GC.ROUTE_PATH_CENTRALIZED_DISPATCH]: () => {
          const guid = G.getGuidFromObject(options);

          if (G.isNotNilAndNotEmpty(guid)) getLoadDispatchBoardDetailsRequest(guid);
        },
        [GC.ROUTE_PATH_DISPATCH_BOARD_CLO]: () => {
          const guid = G.getGuidFromObject(options);

          if (G.isNotNilAndNotEmpty(guid)) {
            getOrderDispatchBoardDetailsRequest(guid);
          } else {
            visitDispatchBoardCloPage();
          }
        },
        [GC.ROUTE_PATH_CUSTOMER_PORTAL]: () => {
          resetCustomerPortalListAndPagination();
          getCustomerPortalListRequest();
        },
        [GC.ROUTE_PATH_CUSTOMER_MASTER_INVOICE_LIST]: () => {
          if (R.equals(componentType, GC.PAGE_DISPATCH_DETAILS_NEW_ORDER)) {
            G.callFunction(options.callback);
          }
        },
        [routesMap.dispatchDetailsLoad(guid)]: () => {
          if (R.equals(componentType, GC.PAGE_CARRIER_PROFILE)) {
            getLoadDriverCarrierRateListRequest();
          }

          if (R.equals(componentType, GC.PAGE_TYPE_CLAIM_DETAILS)) {
            G.callFunction(options.callback);
          }
        },
        [routesMap.dispatchDetailsOrder(guid)]: () => {
          if (R.equals(componentType, GC.PAGE_CARRIER_PROFILE)) {
            getDispatchDetailsOrderRequest(guid);
          }

          if (R.equals(componentType, GC.PAGE_TYPE_CLAIM_DETAILS)) {
            G.callFunction(options.callback);
          }

          if (R.equals(componentType, GC.PAGE_DISPATCH_DETAILS_NEW_ORDER)) {
            G.callFunction(options.callback);
          }
        },
        [routesMap.claimManagementClaimDetails(guid)]: () => {
          visitClaimManagementClaimDetailsPage({ guid });
        },
        [GC.ROUTE_PATH_LOAD_PLANNER]: () => {
          if (dispatchDetailsOpened) {
            resetCloEventListAndPagination();
            refreshCloEventListWithSelectedEvents();
          }
        },
        [GC.ROUTE_PATH_PAYROLLS]: () => {
          if (options) G.callFunction(G.getPropFromObject('closeCallback', options));
        },
      };

      // if (dispatchDetailsOpened) socketTelDisconnectRequest('deactivate');

      setExpandedContainerError(null);

      setExpandedContainerOptions({
        opened: false,
        componentType: null,
        componentsHistory: [],
        fullComponentHistory: [],
      });

      G.callFunction(G.getPropFromObject('scheduleEditorTripDetailsClosed', options));

      if (G.isTrue(notUseVisitPageAction)) return;

      G.callFunction(G.getPropFromObject(routePath, visitPageActionsMap));
    },
    handleChangeComponent: (props) => () => {
      const {
        visitNewDoPage,
        visitFuelCardsPage,
        visitLoadBoardsPage,
        visitTollChargesPage,
        visitDriverCardsPage,
        visitDriverCardsV2Page,
        visitNewDOQuoteEditPage,
        expandedContainerOptions,
        visitCarrierProfilePageV2,
        visitFleetProfileTruckPage,
        visitFleetProfileDriverPage,
        visitFleetProfileVendorPage,
        setExpandedContainerOptions,
        visitDispatchDetailsLoadPage,
        visitFleetProfileTrailerPage,
        visitDispatchDetailsOrderPage,
        visitCarrierContractDetailsPage,
        visitCustomerContractDetailsPage,
        visitClaimManagementClaimDetailsPage,
      } = props;

      const {
        options,
        componentType,
        visitPageGuid,
        componentsHistory,
        fullComponentHistory,
      } = expandedContainerOptions;

      const visitPageActionsMap = {
        [GC.PAGE_NEW_DO]: visitNewDoPage,
        [GC.PAGE_FUEL_CARDS]: visitFuelCardsPage,
        [GC.PAGE_LOAD_BOARDS]: visitLoadBoardsPage,
        [GC.PAGE_TOLL_CHARGES]: visitTollChargesPage,
        [GC.PAGE_DRIVERS_CARD]: visitDriverCardsPage,
        [GC.PAGE_DRIVERS_CARD_V2]: () => visitDriverCardsV2Page({
          pathname: GC.ROUTE_PATH_DRIVER_CARDS_V2,
        }),
        [GC.PAGE_NEW_DO_QUOTE_EDIT]: visitNewDOQuoteEditPage,
        [GC.PAGE_DISPATCH_DETAILS_NEW_LOAD]: visitDispatchDetailsLoadPage,
        [GC.PAGE_TYPE_CLAIM_DETAILS]: visitClaimManagementClaimDetailsPage,
        [GC.PAGE_DISPATCH_DETAILS_NEW_ORDER]: visitDispatchDetailsOrderPage,
        // fleet-v2
        [GC.PAGE_FLEET_TRUCK_PROFILE_V2]: visitFleetProfileTruckPage,
        [GC.PAGE_FLEET_DRIVER_PROFILE_V2]: visitFleetProfileDriverPage,
        [GC.PAGE_FLEET_VENDOR_PROFILE_V2]: visitFleetProfileVendorPage,
        [GC.PAGE_FLEET_TRAILER_PROFILE_V2]: visitFleetProfileTrailerPage,
        // carrier
        [GC.PAGE_CARRIER_PROFILE]: visitCarrierProfilePageV2,
        [GC.PAGE_CARRIER_CONTRACT_DETAILS]: visitCarrierContractDetailsPage,
        [GC.PAGE_CUSTOMER_CONTRACT_DETAILS]: visitCustomerContractDetailsPage,
      };

      setExpandedContainerOptions({
        ...expandedContainerOptions,
        fullComponentHistory: R.append(componentType, R.or(fullComponentHistory, [])),
        componentsHistory: R.append({ componentType, visitPageGuid }, R.or(componentsHistory, [])),
      });

      G.callFunctionWithArgs(
        G.getPropFromObject(componentType, visitPageActionsMap),
        { editPage: true, options, [GC.FIELD_GUID]: visitPageGuid },
      );
    },
  }),
  withHandlers({
    handleCloseExpandedContainerFromKey: ({ handleCloseExpandedContainer }) => (e) => {
      if (R.equals(e.keyCode, GC.EVENT_KEY_CODE_ESC)) handleCloseExpandedContainer();
    },
  }),
  lifecycle({
    componentDidMount() {
      const {
        setZIndex,
        modalOpened,
        handleChangeComponent,
        handleCloseExpandedContainerFromKey,
      } = this.props;

      document.addEventListener('keyup', handleCloseExpandedContainerFromKey);

      handleChangeComponent();

      if (R.not(modalOpened)) {
        setZIndex(11);
      } else {
        setZIndex(1301);
      }
    },
    componentWillUnmount() {
      const { setZIndex } = this.props;

      setZIndex(11);

      document.removeEventListener('keyup', this.props.handleCloseExpandedContainerFromKey);
    },
    componentDidUpdate(prevProps) {
      const {
        location,
        handleChangeComponent,
        expandedContainerOptions,
        handleCloseExpandedContainer,
      } = this.props;

      const pathname = G.getPropFromObject('pathname', location);
      const prevPathname = R.path(['location', 'pathname'], prevProps);

      if (G.notEquals(pathname, prevPathname)) return handleCloseExpandedContainer(true);

      const componentType = G.getPropFromObject('componentType', expandedContainerOptions);
      const prevComponentType = R.path(['expandedContainerOptions', 'componentType'], prevProps);

      const visitPageGuid = G.getPropFromObject('visitPageGuid', expandedContainerOptions);
      const prevVisitPageGuid = R.path(['expandedContainerOptions', 'visitPageGuid'], prevProps);

      if (R.or(
        G.notEquals(componentType, prevComponentType),
        G.notEquals(visitPageGuid, prevVisitPageGuid),
      )) handleChangeComponent();
    },
  }),
  pure,
);

const Component = (({ handleGoBack, expandedContainerOptions, handleCloseExpandedContainer }) => {
  const { error, options, componentType, componentsHistory } = expandedContainerOptions;

  if (G.isNotNilAndNotEmpty(error)) {
    return <ErrorMessage error={error} handleClose={handleCloseExpandedContainer} />;
  }

  const handleGoToPrevComponent = R.gt(R.length(componentsHistory), 1) ? () => handleGoBack() : null;

  const map = {
    [GC.PAGE_NEW_DO]: (
      <NewDOComponent pageWrapperPaddingLeft='0' closeExpandedContainer={handleCloseExpandedContainer} />
    ),
    [GC.PAGE_NEW_DO_QUOTE_EDIT]: (
      <NewQuoteComponent pageWrapperPaddingLeft='0' closeExpandedContainer={handleCloseExpandedContainer} />
    ),
    [GC.PAGE_DRIVERS_CARD]: (
      <DriversCard expandedContainer={true} closeExpandedContainer={handleCloseExpandedContainer} />
    ),
    [GC.PAGE_DRIVERS_CARD_V2]: (
      <Flex width='100%' height='100vh' alignItems='stretch'>
        <Box flexGrow={1} minWidth={0} overflow='hidden'>
          <DriverCardsV2 expandedContainer={true} />
        </Box>
        <Flex
          pt='10px'
          flex='none'
          width='44px'
          alignItems='center'
          flexDirection='column'
          bg={G.getTheme('colors.white')}
          boxShadow='-2px 0 8px rgba(0, 0, 0, 0.12)'
        >
          <Button
            p='0'
            width='32px'
            height='32px'
            borderRadius='50%'
            bg={G.getTheme('colors.white')}
            onClick={handleCloseExpandedContainer}
            title={G.getWindowLocale('actions:close', 'Close')}
            aria-label={G.getWindowLocale('actions:close', 'Close')}
          >
            {I.closeIcon(G.getTheme('colors.dark.blue'), 12)}
          </Button>
        </Flex>
      </Flex>
    ),
    [GC.PAGE_DISPATCH_DETAILS_NEW_LOAD]: (
      <LoadDetailsNewComponent
        {...options}
        expandedContainer={true}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    [GC.PAGE_DISPATCH_DETAILS_NEW_ORDER]: (
      <OrderDetailsNewComponent
        {...options}
        expandedContainer={true}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    [GC.PAGE_LOAD_BOARDS]: (
      <LoadBoards
        expandedContainer={true}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    carrierScorecards: (
      <CarrierScorecards
        overflowY='auto'
        expandedContainer={true}
        height='calc(100vh - 60px)'
        closeExpandedContainer={handleCloseExpandedContainer}
        carrierName={R.path(['options', GC.FIELD_NAME], expandedContainerOptions)}
        carrierGuid={R.path(['options', GC.FIELD_GUID], expandedContainerOptions)}
      />
    ),
    loadHistory: (
      <CarrierRateRequestReport
        height='100vh'
        expandedContainer={true}
        closeExpandedContainer={handleCloseExpandedContainer}
        carrierName={R.path(['options', 'carrierName'], expandedContainerOptions)}
      />
    ),
    loadStatistic: (
      <CarrierStatisticReport
        height='100vh'
        expandedContainer={true}
        closeExpandedContainer={handleCloseExpandedContainer}
        carrierName={R.path(['options', 'carrierName'], expandedContainerOptions)}
      />
    ),
    [GC.PAGE_TYPE_CLAIM_DETAILS]: (
      <ClaimManagementClaimDetails
        expandedContainer={true}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    [GC.PAGE_TOLL_CHARGES]: (
      <TollChargesListComponent pl='0px' closeExpandedContainer={handleCloseExpandedContainer} />
    ),
    [GC.PAGE_FUEL_CARDS]: (
      <FuelCardListComponent pl='0px' closeExpandedContainer={handleCloseExpandedContainer} />
    ),
    // fleet
    [GC.PAGE_FLEET_DRIVER_PROFILE_V2]: (
      <FleetProfileDriver
        expandedContainer={true}
        handleGoBack={handleGoToPrevComponent}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    [GC.PAGE_FLEET_VENDOR_PROFILE_V2]: (
      <FleetProfileVendor
        expandedContainer={true}
        handleGoBack={handleGoToPrevComponent}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    [GC.PAGE_FLEET_TRUCK_PROFILE_V2]: (
      <FleetProfileTruck
        expandedContainer={true}
        handleGoBack={handleGoToPrevComponent}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    [GC.PAGE_FLEET_TRAILER_PROFILE_V2]: (
      <FleetProfileTrailer
        expandedContainer={true}
        handleGoBack={handleGoToPrevComponent}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    // carrier
    [GC.PAGE_CARRIER_PROFILE]: (
      <CarrierProfile
        expandedContainer={true}
        handleGoBack={handleGoToPrevComponent}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    [GC.PAGE_CARRIER_CONTRACT_DETAILS]: (
      <CarrierContractDetails
        expandedContainer={true}
        handleGoBack={handleGoToPrevComponent}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
    [GC.PAGE_CUSTOMER_CONTRACT_DETAILS]: (
      <CustomerContractDetails
        expandedContainer={true}
        handleGoBack={handleGoToPrevComponent}
        closeExpandedContainer={handleCloseExpandedContainer}
      />
    ),
  };

  return (
    <Suspense fallback={<DimmerComponent tableCount={3} rectangleCount={4} />}>
      {map[componentType]}
    </Suspense>
  );
});

const options = {
  dragAxis: 'x',
  minWidth: 800,
  maxWidth: '99vw',
  disableDragging: true,
  enableResizing: { left: true, right: false },
  default: { x: 0, y: 0, width: '70vw', height: '100vh' },
  resizeHandleComponent: {
    left: <ResizeHandleComponent />,
  },
  resizeHandleStyles: {
    left: { cursor: 'ew-resize' },
  },
};

const RndOrFragment = (props) => (
  window.isNotDesktop ?
    <Box width='100%' height='100%'>{props.children}</Box> :
    <Rnd {...props}>{props.children}</Rnd>
);

const ExpandedContainer = (props) => (
  <FixedBox
    top='0'
    width='100%'
    height='100%'
    zIndex={props.zIndex}
    background={G.getTheme('reactModal.overlayBgColor')}
  >
    <AbsoluteBox right='0' height='100vh' width={window.isNotDesktop ? '100vw' : '70vw'}>
      <RndOrFragment {...options}>
        <Component {...props} />
      </RndOrFragment>
    </AbsoluteBox>
  </FixedBox>
);

export default enhance(ExpandedContainer);
